import { createStore, useStore as baseUseStore, Store } from "vuex";
import { InjectionKey } from "vue";
import { GeneralState, InitResponse, Settings } from "@/models/Interfaces";
import {
  BusinessClient,
  Order,
  OrderProduct,
  Customer,
  OrderFilter,
  CouponAvailability,
  Budget,
  Voucher,
  Benefit,
  CardCode,
} from "@/models/Order";
import { api } from "@/services/Api";
import { ProductListItem, Coupon, PriceLine, GetProductPricesResponse, Slot } from "@/models/Product";
import {
  Permissions,
  CouponLimitationType,
  CouponDiscountType,
  CouponType,
  CouponWhoFinances,
} from "@/models/Enums";
import swal from "sweetalert2";
import moment from "moment";
import { ShiftListFilter } from "@/models/Shift";
import { Slot as CalendarSlot } from "@/models/Calendar";
import { FAKE_SLOT_ID_PREFIX } from '@/models/Constants';

export const store = createStore<GeneralState>({
  state: {
    isAuthenticated: false,
    lang: "",
    now: new Date(),
    user: null,
    priceTypes: [],
    currencies: [],
    currencyRates: [],
    products: [],
    banks: [],
    bankBranches: [],
    accounts: [],
    order: new Order(),
    outSideClick: false,
    originOrder: new Order(),
    selectedProduct: null,
    indexTooChangeInBasketProduct: null,
    reloadConfirmed: false,
    reservedOrderCustomer: null,
    orderFilter: new OrderFilter(),
    localCurrencyId: 0,
    languages: [],
    preloaded: null,
    editCustomerMode: false,
    messages: [],
    sumQtyCustomers: 0,
    totalAmount: 0,
    version: "",
    currentLanguageId: 0,
    clubId: 0,
    coupons: [],
    businessClients: [],
    scanValue: "",
    budget: null,
    voucher: new Voucher(),
    orderSources: [],
    autoPrintReceipt: false,
    orderValidation: null,
    couponTransactionId: null,
    printerSettings: null,
    createdVirtualSlotIds: [],
    budgets: [],
    searches: [],
    isMoreCoupons: false,
    shiftFilter: new ShiftListFilter(),
    virtualSlots: []
  },
  getters: {
    printerSettings: (state): Settings | null => state.printerSettings,
    currentLanguage: (state): string => state.lang,
    productsByPriceType: (state): ProductListItem[] =>
      state.products.filter((x) =>
        x.productPrices.some((pp) => pp.priceTypeId == state.order.priceType)
      ),
    orderBusinessClientExist: (state): boolean =>
      state.order.businessClient != null,
    canSellToPrivate(state): boolean {
      if (
        !state.user ||
        !state.user.permissions == null ||
        state.user.permissions.length == 0
      ) {
        return false;
      }
      return state.user.permissions.includes(Permissions.CashierSellToPrivate);
    },
    canSellToBusiness(state): boolean {
      if (
        !state.user ||
        !state.user.permissions == null ||
        state.user.permissions.length == 0
      ) {
        return false;
      }
      return state.user.permissions.includes(Permissions.CashierSellToBusiness);
    },
    canEditSlot(state): boolean {
      if (
        !state.user ||
        !state.user.permissions == null ||
        state.user.permissions.length == 0
      ) {
        return false;
      }
      return state.user.permissions.includes(Permissions.CashierEditSLot);
    },
    canEditDiscount(state): boolean {
      if (
        !state.user ||
        !state.user.permissions == null ||
        state.user.permissions.length == 0
      ) {
        return false;
      }
      return state.user.permissions.includes(Permissions.CashierChangeDiscount);
    },
    canEditRate(state): boolean {
      if (
        !state.user ||
        !state.user.permissions == null ||
        state.user.permissions.length == 0
      ) {
        return false;
      }
      return state.user.permissions.includes(Permissions.CashierChangeRate);
    },
    changeUnitPrice(state): boolean {
      if (
        !state.user ||
        !state.user.permissions == null ||
        state.user.permissions.length == 0
      ) {
        return false;
      }
      return state.user.permissions.includes(Permissions.ChangeUnitPrice);
    },
    canCreateChangeOrderInPast(state): boolean {
      if (
        !state.user ||
        !state.user.permissions == null ||
        state.user.permissions.length == 0
      ) {
        return false;
      }
      return state.user.permissions.includes(
        Permissions.CreateChangeOrderInPast
      );
    },
    canCreateChangeSlotInPast(state): boolean {
      if (
        !state.user ||
        !state.user.permissions == null ||
        state.user.permissions.length == 0
      ) {
        return false;
      }
      return state.user.permissions.includes(
        Permissions.CreateChangeSlotInPast
      );
    },
    orderPaid(state): number {
      return state.order.payment.payments.reduce((acc, curr) => {
        return (acc += Number(curr.amount) * curr.localCurrencyRate);
      }, 0);
    },
    orderTotal(state): number {
      return getCustomerTotalToPay(state.order.products, state.order.businessClient?.isSubsidized ?? false) * (state.currencyRates.find((x) => x.currencyId == state.order.currencyId)?.rate ?? 1);
    },
    orderBalance(state, { orderPaid, orderTotal }): number {
      return orderTotal - orderPaid;
    },
    preloadedPrices(state) {
      return state.preloaded?.prices;
    },
    preloadedSlots(state) {
      return state.preloaded?.slots;
    },
    getCouponsByOrderBusinessClient(state): Coupon[] {
      let result: Coupon[] = [];
      if (state.order?.businessClientId) {
        result = state.coupons.filter(
          (x) => x.clubIds.includes(state.order.businessClientId ?? 0) || x.businessClientTypeId == state.order?.businessClient?.businessClientTypeId
        );
      }
      return result;
    },
  },
  mutations: {
    init(state, data: InitResponse): void {
      state.isAuthenticated = data.isAuthenticated;
      state.lang = data.lang;
      state.user = data.user;
      state.priceTypes = data.priceTypes;
      state.products = data.products;
      state.currencies = data.currencies;
      state.currencyRates = data.currencyRates;
      state.banks = data.banks;
      state.bankBranches = data.bankBranches;
      state.accounts = data.accounts;
      state.localCurrencyId = data.localCurrencyId;
      state.languages = data.languages;
      state.messages = data.messages;
      state.sumQtyCustomers = data.sumQtyCustomers;
      state.totalAmount = data.totalAmount;
      state.version = data.version;
      state.currentLanguageId = data.currentLanguageId;
      state.coupons = data.coupons;
      state.businessClients = data.businessClients;
      state.orderSources = data.orderSources;
      state.now = moment(data.dateNow).toDate();
      state.autoPrintReceipt = data.autoPrintReceipt;
    },
    setPreload(state, payload) {
      state.preloaded = payload;
    },
    addPreloadSlot(state, slot: Slot){
      if (state.preloaded) {
        if (state.preloaded.slots.slots != null) {
          state.preloaded.slots.slots.push(slot);
        } else {
          state.preloaded.slots.slots = [slot];        
        }        
      }
    },
    updatePreloadPrices(state, prices: GetProductPricesResponse | null): void{
      if(state.preloaded && prices){
        state.preloaded.prices = prices;
      }
    },
    updateLang(state, lang: string): void {
      state.lang = lang;
    },
    setPrinterSettings(state, settings: Settings | null) {
      state.printerSettings = settings;
    },
    updateTime(state) {
      state.now = new Date(addMinutes(state.now, 1));
      function addMinutes(date, minutes) {
        date.setMinutes(date.getMinutes() + minutes);
        return date;
      }
    },
    setReservedOrderCustomer(state, customer: Customer) {
      state.reservedOrderCustomer = customer;
    },
    setSelectedProduct(state, { date, productId, slot }) {
      if (productId == null) {
        state.selectedProduct = null;
        return;
      }
      state.selectedProduct = {
        date,
        productId,
        slotId: slot.id,
        slotStartTime: slot.startTime,
        slotEndTime: slot.endTime,
        quantity: slot.quantity,
        slotDuration: slot.duration,
        slotFakeId: slot.slotFakeId
      };
    },
    async updateChangetSlot(state, { productId, slotId, date, newSlotId }) {
      state.order?.products?.forEach((product) => {
        if (
          product.slotId == slotId &&
          product.id == productId &&
          product.date == date
        ) {
          product.slotId = newSlotId;
        }
      });
    },
    async updateOrderProduct(
      state,
      { productId, priceLine, slot, date, channelId, priceId, currencyId, cardCode = "" }
    ) {
      const productWithSamePrice = state.order.products.find(
        (x) =>
          x.id == productId &&
          x.priceLine?.id == priceLine.id &&
          x.date == date &&
          x.slotId == slot.id &&
          x.slotStartTime == slot.startTime &&
          x.slotEndTime == slot.endTime &&
          x.priceLine.couponId == priceLine.couponId
      );

      if (productWithSamePrice) {
        if (productWithSamePrice.notValidCoupon) {
          return;
        }

        // Check if the cardCode is not empty and if it is not already in the productWithSamePrice.cardCode add it to the list
        if (cardCode) {
          const newCard = new CardCode(cardCode, priceLine.quantity + 1, priceLine.coupon?.couponCode);
          const existingCardCodes = productWithSamePrice.cardCode
            ? JSON.parse(productWithSamePrice.cardCode).map((code) => new CardCode(code.code, code.amount, code.couponCode))
            : [];

          const existingCard = existingCardCodes.find((existing) => existing.code === newCard.code && existing.couponCode === newCard.couponCode);
          if (existingCard) {
            existingCard.amount += newCard.amount;
            existingCard.couponCode = newCard.couponCode;
          } else {
            existingCardCodes.push(newCard);
          }

          productWithSamePrice.cardCode = JSON.stringify(existingCardCodes);
        }
        
        productWithSamePrice.priceLine.quantity += priceLine.quantity + 1;
        await store.dispatch("couponCalculateAddQty", {
          priceLine: productWithSamePrice.priceLine,
          qty: priceLine.quantity + 1,
        });
        // store.commit("calculateProductDiscountByBusinessClient", {
        //   product: productWithSamePrice,
        //   suitableCombinations: null,
        // });
        return;
      }

      const product = state.products.find((x) => x.id == productId);
      if (!product) {
        return;
      }

      if (state.order.currencyId && state.order.currencyId != currencyId) {
        swal.fire({
          icon: "error",
          text: "Price has different currencies",
        });
        return;
      } else {
        state.order.currencyId = currencyId;
      }

      if (!cardCode) {
        const canContinue = await store.dispatch(
          "CouponClaculateWhenAddProduct",
          {
            priceLine,
          }
        );      
        if (!canContinue) {
          return;
        }
      }

      let discount = 0;
      if (priceLine.couponId) {

        if (priceLine.coupon.discountAmount) {
          discount = (priceLine.quantity + 1) * priceLine.coupon.discountAmount;
        } else if (priceLine.coupon.ticketFixedUnitPrice) {
          priceLine.priceForOne = priceLine.coupon.ticketFixedUnitPrice;
        } else if (priceLine.coupon.discountPercent) {
          discount =
            ((((priceLine.quantity + 1) * priceLine.priceForOne) / 100) * priceLine.coupon.discountPercent);
        }
      }
      const newP = new OrderProduct(
        product.id,
        product.name,
        channelId,
        priceId,
        priceLine.priceForOne,
        cardCode ? JSON.stringify([new CardCode(cardCode, priceLine.quantity + 1, priceLine.coupon.couponCode)]) : '',
      )
        .withPrice(priceLine)
        .withSlot(slot)
        .withDate(date)
        .withDiscount(discount);

      state.order.products.unshift(newP);
      await store.dispatch("couponCalculateAddQty", {
        priceLine,
        qty: priceLine.quantity,
      });
    },
    setOrder(state, order: Order) {
      state.order = order;
    },
    setOriginOrder(state, order: Order) {
      state.originOrder = order;
    },
    clearOrder(state) {
      state.order = new Order();
    },
    setArrivalConfirmationType(state, arrivalConfirmationType: number) {
      state.order.arrivalConfirmation = arrivalConfirmationType;
    },
    updateProductsByBusinessClient(state) {
      if (state.order == null) {
        return;
      }
      if (state.order.products.length == 0) {
        return;
      }
      state.order.products.forEach((productElement) => {
        productElement.priceLine.discount = 0;
      });
      if (state.order.businessClient == null) {
        return;
      }
      if (
        state.order.businessClient.businessClientDiscountCombinations.length ==
        0
      ) {
        return;
      }
      const suitableCombinations =
        state.order.businessClient.businessClientDiscountCombinations.filter(
          (x) =>
            x.currencyId == state.order.currencyId &&
            x.priceTypeId == state.order.priceType &&
            state.order.products.map((p) => p.channelId).includes(x.channelId)
        );
      if (suitableCombinations.length == 0) {
        return;
      }
      for (let j = 0; j < state.order.products.length; j++) {
        store.commit("calculateProductDiscountByBusinessClient", {
          product: state.order.products[j],
          suitableCombinations,
        });
      }
    },
    calculateProductDiscountByBusinessClient(
      state,
      { product, suitableCombinations }
    ) {
      if (!suitableCombinations) {
        suitableCombinations =
          state.order.businessClient?.businessClientDiscountCombinations.filter(
            (x) =>
              x.currencyId == state.order.currencyId &&
              x.priceTypeId == state.order.priceType &&
              state.order.products.map((p) => p.channelId).includes(x.channelId)
          );
      }
      if (!suitableCombinations) {
        return;
      }
      for (let i = 0; i < suitableCombinations.length; i++) {
        if (product.priceLine.quantity == 0) {
          continue;
        }
        if (product.channelId != suitableCombinations[i].channelId) {
          continue;
        }
        if (state.order.priceType != suitableCombinations[i].priceTypeId) {
          continue;
        }
        const total =
          product.priceLine.quantity * (product.priceLine?.priceForOne ?? 0);
        product.priceLine.discount = Math.round(
          (total / 100) * suitableCombinations[i].discount
        );
      }
    },
    async plusQuontityToOrderProduct(state, index: number) {
      const product = state.order.products[index];
      const canContinue = await store.dispatch(
        "CouponClaculateWhenAddProduct",
        {
          priceLine: product.priceLine,
        }
      );

      if (!canContinue) {
        return;
      }
      product.priceLine.quantity++;
      store.dispatch("couponCalculateAddQty", { priceLine: product.priceLine });
    },
    plusDiscountToOrderProduct(state, index: number) {
      if (!store.getters.canEditDiscount) {
        return;
      }
      const product = state.order.products[index];
      const productTotal =
        product.priceLine.priceForOne * product.priceLine.quantity;
      const productTotalPercent =
        (100 * product.priceLine.discount) / productTotal;
      if (!state.user || !state.user.cashierMaxDicountPercent) {
        return;
      }
      if (state.user.cashierMaxDicountPercent <= productTotalPercent) {
        return;
      }

      const total =
        (product.priceLine?.priceForOne ?? 0) * product.priceLine.quantity;

      if (product.priceLine.discount == total) {
        return;
      }

      product.priceLine.discount++;
    },
    minusDiscountToOrderProduct(state, index: number) {
      const product = state.order.products[index];
      if (!store.getters.canEditDiscount) {
        return;
      }
      if (product.priceLine.discount < 1) {
        return;
      }

      product.priceLine.discount--;
    },
    changeOutSideClick(state, newValue: boolean) {
      state.outSideClick = newValue;
    },
    toChangeInBasketProduct(state, index: number) {
      state.indexTooChangeInBasketProduct = index;
    },
    changePriceForOne(state, { index, newValue }) {
      state.order.products[index].priceLine.priceForOne = newValue;
    },
    async changeInVoucherCouponSlot(
      state,
      { slotId, productId, benefitId, dateStart, benefitCardCode }
    ) {
      if (!slotId) {
        return;
      }

      let benefit: Benefit | undefined = undefined;
      if(state.isMoreCoupons ?? false){
        const budgets = state.budgets.filter((x) => x.cardCode == benefitCardCode) ?? [];
        
        if(budgets.length > 0){
          benefit = budgets[0].benefitList.find((x) => x.benefitId == benefitId);
        }
      }else{
        benefit = state.budget?.benefitList.find(
          (x) => x.benefitId == benefitId
        );
      }

      
      const orderProducts = state.voucher.products.filter(
        (x) =>
          x.cardCode == benefitCardCode &&
          x.id == productId &&
          benefit?.couponCodes.includes(x.priceLine.coupon?.couponCode ?? "")
      );

      if (orderProducts == null || !orderProducts.length) {
        return;
      }

      const productPriceLine = orderProducts[0].priceLine;

      const slotResponse = await api
        .withLoader()
        .getProductSlots(
          productId,
          productPriceLine.coupon?.priceTypeIds ?? [0],
          dateStart,
          0
        );

      const slot = slotResponse.data?.slots.find((x) => x.id == slotId);

      if (slot == null) {
        return;
      }

      if(productPriceLine.coupon){
        const coupon = productPriceLine.coupon;
        const start = new Date(dateStart);
        const from = new Date(coupon.validDateFrom);
        const to = new Date(coupon.validDateTo);

        if(start < from || start > to){
          swal.fire({
            icon: "error",
            text: `Expiration date of coupon is ${moment(coupon.validDateTo).format('DD/MM/YYYY')}, slot date must be until this date`
          });
          return;
        }
      }

      for (const orderProduct of orderProducts) {
        orderProduct.slotId = Number(slot.id);
        orderProduct.slotStartTime = slot.startTime;
        orderProduct.slotEndTime = slot.endTime;
        orderProduct.date = dateStart;
      }

      state.indexTooChangeInBasketProduct = null;
    },
    async changeInBasketProductSlot(state, { slotId, dateStart }) {
      if (!slotId) {
        return;
      }
      if (state.indexTooChangeInBasketProduct == null) {
        return;
      }
      const product = state.order.products[state.indexTooChangeInBasketProduct];
      if (!product.slotId) {
        return;
      }
      let slot: Slot | undefined = undefined;
      if (slotId.toString().startsWith(FAKE_SLOT_ID_PREFIX)) {
        slot = await store.dispatch("getVirtualSlot", slotId);
      } else {
        const slotResponse = await api
          .withLoader()
          .getProductSlots(product.id, [state.order.priceType], dateStart, 0);

        slot = slotResponse.data?.slots.find((x) => x.id == slotId);
      }

      if (slot == null) {
        return;
      }

      let prevSlotId: number | null = null;

      const duplicateIndex = state.order.products.findIndex(
        (x) =>
          x.date == dateStart &&
          x.slotId == slot?.id &&
          x.id == product.id &&
          x.priceLine?.id == product.priceLine?.id
      );
      if (
        duplicateIndex >= 0 &&
        duplicateIndex != state.indexTooChangeInBasketProduct
      ) {
        if (state.order?.products[duplicateIndex]?.priceLine?.quantity) {
          state.order.products[duplicateIndex].priceLine.quantity +=
            product.priceLine?.quantity ?? 0;
        }
        prevSlotId = product.slotId;
        state.order.products.splice(state.indexTooChangeInBasketProduct, 1);
      } else {
        prevSlotId = product.slotId;
        product.slotDuration = slot.duration;
        product.slotId = Number(slot.id);
        product.slotStartTime = slot.startTime;
        product.slotEndTime = slot.endTime;
        product.date = dateStart;
        product.slotFakeId = slot.slotFakeId;
      }
      state.indexTooChangeInBasketProduct = null;

      if (!state.order.products.find((x) => x.slotId == prevSlotId)) {
        await api.withLoader().deleteCalendarSlot(prevSlotId);
      }
    },
    reloadConfirm(state) {
      state.reloadConfirmed = true;
    },
    setReservOrderFilter(state, orderFilter: OrderFilter) {
      state.orderFilter = orderFilter;
    },
    setEditCustomerMode(state, newVal: boolean) {
      state.editCustomerMode = newVal;
    },
    removeMessage(state, id: number) {
      state.messages = state.messages.filter((x) => x.id != id);
    },
    setClubId(state, clubId: number) {
      state.clubId = clubId;
    },
    setClubInOrder(state, clubId: number) {
      state.order.businessClient =
        state.businessClients.find((x) => x.id == clubId) ?? null;
      state.order.businessClientId = clubId;
    },
    showAllOrderPriceLines(state) {
      state.order.showAllPriceLines = !state.order.showAllPriceLines;
    },
    setVoucher(state, voucher: Voucher) {
      state.voucher = voucher;
    },
    setBudget(state, budget: Budget) {
      state.budget = budget;
    },
    setBudgets(state, budgets: Budget[]) {
      state.budgets = budgets;
    },
    setHistorySearches(state, values: string[]) {
      state.searches = values;
    },
    setIsMoreCoupons(state, isMoreCoupons: boolean) {
      state.isMoreCoupons = isMoreCoupons;
    },
    addVirtualSlot(state, slot: CalendarSlot) {
      state.virtualSlots = [...state.virtualSlots, slot]
    },
    clearVirtualSlots(state) {
      state.virtualSlots = [];
    }
  },
  actions: {
    async setOrderPriceType({ state, dispatch }, priceType: number) {
      for (let i = state.order.products.length - 1; i >= 0; i--) {
        await dispatch("removeProductFromOrder", i);
      }
      state.order = new Order();
      state.order.priceType = priceType;
    },
    async removeProductFromOrder({ state, dispatch }, index: number) {
      if (state.order.products.length) {
        const product = state.order.products[index];
        if (product?.priceLine?.coupon) {
          const coupon = state.coupons.find(
            (x) => x.id == product.priceLine.coupon?.id
          );
          if (coupon) {
            if (coupon.limitationType == CouponLimitationType.CustomerQuantity) {
              coupon.leftLimitationTotal += product.priceLine.customerQuantity;
            } else {
              if (store.state.scanValue) {
                coupon.leftLimitationTotal += product.priceLine.quantity;
              } else {
                const usedLimit = await getUsedLimitForRemoveProduct({
                  coupon,
                  product,
                });
                coupon.leftLimitationTotal += usedLimit;
              }
            }
          }
        }        

        // Restore QTY for vaucher products
        const cardCodes = product.cardCode ? JSON.parse(product.cardCode) : null;

        if(cardCodes){
          cardCodes.forEach((code) => {
            const currentProductVoucher = state.voucher.products.find((x) => x.cardCode == code.code && x.id == product.id && x.priceLine?.coupon?.couponCode == code.couponCode);
            if(currentProductVoucher){
              currentProductVoucher.priceLine.quantity += code.amount;
            }
          });
        }
        
        state.order.products.splice(index, 1);
      }
      if (!state.order.products.length) {
        state.order.currencyId = 0;
      }
      state.indexTooChangeInBasketProduct = null;
    },
    async CouponClaculateWhenAddProduct({ state }, { priceLine }) {
      if (!priceLine.couponId) return true;

      const coupon = state.coupons.find((x) => x.id === priceLine.couponId);
      if (!coupon) return false;

      const isLimitAvailable = (amount) =>
        limitAvailable(
          amount,
          coupon.leftLimitationTotal
        );

      if (coupon.limitationType === CouponLimitationType.CustomerQuantity) {
        return coupon.leftLimitationTotal >= priceLine.qtyToUse;
      }

      if (coupon.discountAmount) return isLimitAvailable(coupon.discountAmount);
      if (coupon.ticketFixedUnitPrice) return isLimitAvailable(coupon.ticketFixedUnitPrice);
      if (coupon.discountType === CouponDiscountType.NoDiscount) {
        return isLimitAvailable(priceLine.priceForOne);
      }

      if (coupon.discountPercent) {
        const discount = (priceLine.priceForOne / 100) * coupon.discountPercent;

        return coupon.leftLimitationTotal >= discount;
      }

      return true;
    },
    startTime({ commit, state }) {
      const timeout = 60 - state.now.getSeconds();

      setTimeout(() => {
        commit("updateTime");
        setInterval(() => {
          commit("updateTime");
        }, 1000 * 60);
      }, 1000 * timeout);
    },
    async loadProducts({ state }) {
      const response = await api.getRefProducts();
      state.products = response.data ?? [];
    },
    async checkDiscountByRestriction({ state }, { product, newDiscountValue }) {
      let discount = Number(newDiscountValue.toString().split(",").join(""));
      const productTotal =
        product.priceLine.priceForOne * product.priceLine.quantity;
      const productTotalPercent = (100 * discount) / productTotal;
      if (
        state.user &&
        state.user.cashierMaxDicountPercent &&
        state.user.cashierMaxDicountPercent < productTotalPercent
      ) {
        discount = Math.round(
          (productTotal * state.user.cashierMaxDicountPercent) / 100
        );
      }
      product.priceLine.discount = discount;
      return discount;
    },
    async changePriceLineInBusketProduct(
      { commit, dispatch, state },
      { productId, priceLine, slot, date, channelId, priceId, currencyId }
    ) {
      if (state.indexTooChangeInBasketProduct == null) {
        commit("updateOrderProduct", {
          productId,
          priceLine,
          slot,
          date,
          channelId,
          priceId,
          currencyId,
        });
        return;
      }
      const product = state.order.products[state.indexTooChangeInBasketProduct];
      if (!product) {
        return;
      }
      const duplicateIndex = state.order.products.findIndex(
        (x) =>
          x.id == product.id &&
          x.date == product.date &&
          x.slotId == product.slotId &&
          x.id == product.id &&
          x.priceLine?.id == priceLine?.id
      );
      if (
        duplicateIndex >= 0 &&
        duplicateIndex != state.indexTooChangeInBasketProduct
      ) {
        if (state.order.products[duplicateIndex].priceLine.couponId) {
          swal.fire({
            icon: "error",
            text: "You can't turn this item into coupon",
          });
          return;
        }
        state.order.products[duplicateIndex].priceLine.quantity +=
          product.priceLine.quantity;
        dispatch("removeProductFromOrder", state.indexTooChangeInBasketProduct);
      } else {
        if (product.priceLine.id != priceLine.id) {
          if (product.priceLine.couponId) {
            const coupon = state.coupons.find(
              (x) => x.id == product.priceLine.couponId
            );
            if (coupon) {
              if (coupon.limitationType == CouponLimitationType.CustomerQuantity) {
                coupon.leftLimitationTotal += product.priceLine.customerQuantity;
              } else {
                const discount = await getUsedLimitForChangePriceLine({
                  coupon,
                  product,
                });
                coupon.leftLimitationTotal += discount;
              }
            }
          }
          if (priceLine.couponId) {
            const canContinue = await store.dispatch(
              "CouponClaculateWhenAddProduct",
              {
                priceLine: priceLine,
              }
            );
            if (!canContinue) {
              return;
            }
          }
          product.priceLine = priceLine;
          product.priceLine.quantity = 1;
          store.dispatch("couponCalculateAddQty", {
            priceLine: product.priceLine,
          });
        }
      }
      state.indexTooChangeInBasketProduct = null;
    },
    getCurrencyRate({ state }, id: number | null) {
      return state.currencyRates.find((x) => x.currencyId == id)?.rate ?? 1;
    },
    async couponCalculateAddQty({ state, dispatch }, { priceLine, qty, cardCode }) {
      if (!priceLine.coupon) {
        return;
      }

      const coupon = state.coupons.find((x) => x.id == priceLine.coupon?.id);
      if (!coupon) {
        return;
      }

      // Calculate Discount
      if (coupon.discountPercent) {
        priceLine.discount =
          ((priceLine.quantity * priceLine.priceForOne) / 100) *
          coupon.discountPercent;
      } else if (coupon.ticketFixedUnitPrice) {
        priceLine.priceForOne = coupon.ticketFixedUnitPrice;
      } else if (coupon.discountAmount) {
        priceLine.discount = priceLine.quantity * coupon.discountAmount;
      }

      // Calculate Coupon limitation
      if (coupon.limitationType == CouponLimitationType.CustomerQuantity) {
        if (qty) {
          coupon.leftLimitationTotal -= qty * priceLine.qtyToUse;
        } else {
          coupon.leftLimitationTotal -= priceLine.qtyToUse;
        }
      } else {
        if (store.state.scanValue) {
          if (qty) {
            coupon.leftLimitationTotal -= qty;
          } else {
            coupon.leftLimitationTotal--;
          }
        } else {
          let discount = 0;
          if (coupon.discountPercent) {
            discount = (priceLine.priceForOne / 100) * coupon.discountPercent;
          } else if (coupon.ticketFixedUnitPrice) {
            discount = coupon.ticketFixedUnitPrice;
          } else if (coupon.discountAmount) {
            discount = coupon.discountAmount;
          } else if (coupon.discountType == CouponDiscountType.NoDiscount) {
            discount = priceLine.priceForOne;
          }
          coupon.leftLimitationTotal -= discount;
        }
      }
    },
    async minusQuontityToOrderProduct({ state, dispatch }, index: number) {
      const product = state.order.products[index];
      if (product.priceLine?.quantity == 1) {
        return;
      }
      if (product.priceLine.coupon) {
        const coupon = state.coupons.find(
          (x) => x.id == product.priceLine.coupon?.id
        );
        if (!coupon) {
          return;
        }

        // Calculate Coupon limitation
        let discount = 0;
        if (coupon.limitationType == CouponLimitationType.CustomerQuantity) {
          coupon.leftLimitationTotal += product.priceLine.qtyToUse;
        } else {
          if (store.state.scanValue) {
            coupon.leftLimitationTotal++;
          } else {
            if (coupon.discountPercent) {
              discount =
                (((product.priceLine.priceForOne) / 100) * coupon.discountPercent);
            } else if (coupon.ticketFixedUnitPrice) {
              discount = coupon.ticketFixedUnitPrice;
            } else if (coupon.discountAmount) {
              discount = coupon.discountAmount;
            } else if (coupon.discountType == CouponDiscountType.NoDiscount) {
              discount = product.priceLine.priceForOne;
            }
            coupon.leftLimitationTotal += discount;
          }
        }

        // Calculate Discount
        if (coupon.discountPercent) {
          discount =
            (product.priceLine.priceForOne / 100) * coupon.discountPercent;
        } else if (coupon.discountAmount) {
          discount = coupon.discountAmount;
        } else {
          discount = 0;
        }

        product.priceLine.discount = product.priceLine.discount - discount;
      }
      product.priceLine.quantity--;
      if (!product.priceLine.coupon) {
        store.dispatch("checkDiscountByRestriction", {
          product,
          newDiscountValue: product.priceLine.discount,
        });
      }

      //   store.commit("calculateProductDiscountByBusinessClient", {
      //     product: product,
      //     suitableCombinations: null,
      //   });
    },
    async clearCouponsFromBasket({ state }) {
      state.order.businessClient = null;
      state.order.businessClientId = null;
      for (let j = 0; j < state.order.products.length; j++) {
        const product = state.order.products[j];
        if (product.priceLine.couponId) {
          product.notValidCoupon = true;
        }
      }
    },
    async orderProductsContainsCoupons(
      { state, dispatch },
      businessClient: BusinessClient
    ): Promise<boolean> {
      let contains = false;
      if (!state.order.products || !state.order.products.length) {
        return contains;
      }
      const coupons = (await dispatch(
        "getCouponsByBusinessClient",
        businessClient
      )) as Coupon[];
      coupons.forEach((coupon) => {
        const product = state.order.products.find(
          (x) =>
            coupon.productIds.includes(x.id) &&
            coupon.customerTypes.includes(x.priceLine.id) &&
            moment(x.date).isSameOrAfter(moment(coupon.validDateFrom)) &&
            moment(x.date).isSameOrBefore(moment(coupon.validDateTo))
        );

        if (product) {
          contains = true;
        }
      });
      return contains;
    },
    async setOrderBusinessClient({ state, dispatch }, client: BusinessClient) {
      const coupons = (await dispatch(
        "getCouponsByBusinessClient",
        client
      )) as Coupon[];
      for (let j = 0; j < state.order.products.length; j++) {
        const product = state.order.products[j];
        if (product.priceLine.couponId) {
          if (coupons.find((x) => x.id == product.priceLine.couponId)) {
            product.notValidCoupon = false;
          } else {
            product.notValidCoupon = true;
          }
        } else {
          if (
            coupons.find((x) => x.customerTypes.includes(product.priceLine.id))
          ) {
            product.notValidCoupon = true;
          }
        }
      }

      state.order.businessClient = client;
      state.order.businessClientId = client.id;
      //   store.commit("updateProductsByBusinessClient");
    },
    getCouponsByBusinessClient({ state }, client: BusinessClient): Coupon[] {
      return state.coupons.filter((x) => x.clubIds.includes(client.id) || x.businessClientTypeId == client.businessClientTypeId);
    },
    validateOrder(
      { state },
      { budgets, order }: { order: Order; budgets: Budget[] | null }
    ) {
      state.orderValidation = api.ValidateOrder(order, budgets);
    },
    cancelChanges({ state }) {
      state.order.payment.bankTransferPayments =
        state.order.payment.bankTransferPayments.filter((x) => x.id);
      state.order.payment.cashPayments =
        state.order.payment.cashPayments.filter((x) => x.id);
      state.order.payment.creditCardPayments =
        state.order.payment.creditCardPayments.filter((x) => x.id);
      state.order.payment.checkPayments =
        state.order.payment.checkPayments.filter((x) => x.id);
    },
    getVirtualSlot({ state }, id: string) {
      return state.virtualSlots.find((x) => x.slotFakeId == id);
    },
    getVirtualSlots({ state }) {
      return state.virtualSlots;
    },
  },
});

function limitAvailable(
  amount,
  leftLimitationTotal
) {
  return (
    leftLimitationTotal >=
    amount
  );
}

export const key: InjectionKey<Store<GeneralState>> = Symbol();

export function useStore() {
  return baseUseStore(key);
}

async function getUsedLimitForRemoveProduct({
  coupon,
  product,
}) {
  let usedLimit = 0;
  if (coupon.discountPercent) {
    usedLimit =
      (((product.priceLine.priceForOne / 100) * coupon.discountPercent)) * product.priceLine.quantity;
  } else if (coupon.ticketFixedUnitPrice) {
    usedLimit = coupon.ticketFixedUnitPrice * product.priceLine.quantity;
  } else if (coupon.discountAmount) {
    usedLimit = coupon.discountAmount * product.priceLine.quantity;
  } else if (coupon.discountType == CouponDiscountType.NoDiscount) {
    usedLimit = product.priceLine.priceForOne * product.priceLine.quantity;
  }
  return usedLimit;
}

async function getUsedLimitForChangePriceLine({ coupon, product }) {
  let discount = 0;
  if (coupon.discountPercent) {
    const priceForAll = product.priceLine.priceForOne * product.priceLine.quantity;
    discount = (priceForAll / 100) * coupon.discountPercent;
  } else if (coupon.ticketFixedUnitPrice) {
    discount = coupon.ticketFixedUnitPrice * product.priceLine.quantity;
  } else if (coupon.discountAmount) {
    discount = coupon.discountAmount * product.priceLine.quantity;
  } else if (coupon.discountType == CouponDiscountType.NoDiscount) {
    discount = product.priceLine.priceForOne * product.priceLine.quantity;
  }
  return discount;
}

export function getCustomerTotalToPay(products: OrderProduct[], isSubsidized: boolean): number {
  return products.reduce((acc, curr) => {
    let customerTotalAmount = 0;
    
    // Check if priceLine and coupon exist and if coupon has products
    if (curr.priceLine?.coupon) {
      customerTotalAmount = getCustomerTotalAmountByCoupon(
        curr.priceLine.coupon,
        curr.priceLine?.priceForOne ?? 0,
        curr.priceLine?.quantity ?? 0,
        curr.priceLine?.discount ?? 0
      );
    } else if (isSubsidized) {
      // If no coupon products and isSubsidized is true, return 0 for this item
      customerTotalAmount = 0;
    } else {
      // Original calculation when no coupon and not subsidized
      customerTotalAmount =
        (curr.priceLine?.priceForOne ?? 0) * (curr.priceLine?.quantity ?? 0) -
        (curr.priceLine?.discount ?? 0);
    }
    
    return acc + customerTotalAmount;
  }, 0);
}

function getCustomerTotalAmountByCoupon(
  coupon: Coupon,
  unitPrice: number,
  qty: number,
  discount: number
): number {
  let customerTotalAmount = 0;
  switch (coupon.couponType) {
    case CouponType.BusinessClientAgreement:
      if (coupon.discountType == CouponDiscountType.Discount) {
        if (coupon.whoFinances == CouponWhoFinances.NoFinance) {
          customerTotalAmount = unitPrice * qty - discount;
        }
        if (coupon.whoFinances == CouponWhoFinances.Discount) {
          customerTotalAmount = unitPrice * qty - discount;
        }
      }

      if (
        coupon.discountType == CouponDiscountType.TicketFixedUnitPrice &&
        coupon.whoFinances == CouponWhoFinances.NoFinance
      ) {
        customerTotalAmount = unitPrice * qty - discount;
      }

      break;
    case CouponType.Regular:
    case CouponType.NextPurchase:
      customerTotalAmount = unitPrice * qty - discount;
      break;
  }
  
  return customerTotalAmount;
}
